import React, { useRef, useState } from "react";
import {
  Box, Button, Chip, Divider, Grid, IconButton, MenuItem, Paper, Stack, Tab, Tabs,
  TextField, Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import {
  useGetTemplatesQuery, useGetTemplateVariablesQuery, useCreateTemplateMutation,
  useUpdateTemplateMutation, useDeleteTemplateMutation, usePreviewTemplateMutation,
  useAiGenerateTemplateMutation,
} from "../../../Api/platformApi";

const CHANNELS = ["email", "sms", "whatsapp"];
const CATEGORIES = ["billing", "invoice", "payment", "customer", "lead", "appointment", "system", "security", "marketing"];

const blank = { id: null, name: "", channel: "email", category: "customer", visibility: "customer", subject: "", body: "" };

const Templates: React.FC = () => {
  const { data: tplData } = useGetTemplatesQuery();
  const { data: varsData } = useGetTemplateVariablesQuery();
  const [createTemplate] = useCreateTemplateMutation();
  const [updateTemplate] = useUpdateTemplateMutation();
  const [deleteTemplate] = useDeleteTemplateMutation();
  const [preview] = usePreviewTemplateMutation();
  const [aiGenerate, { isLoading: aiLoading }] = useAiGenerateTemplateMutation();

  const [form, setForm] = useState<any>(blank);
  const [tab, setTab] = useState(0);
  const [rendered, setRendered] = useState<{ subject: string; body: string } | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  const templates = tplData?.data ?? [];
  const grouped = varsData?.grouped ?? {};

  const edit = (t: any) => { setForm({ ...t, subject: t.subject ?? "" }); setTab(0); setRendered(null); };
  const reset = () => { setForm(blank); setRendered(null); };

  const insertVariable = (key: string) => {
    const token = `{{${key}}}`;
    const el = bodyRef.current;
    if (!el) { setForm((f: any) => ({ ...f, body: f.body + token })); return; }
    const start = el.selectionStart ?? form.body.length;
    const end = el.selectionEnd ?? form.body.length;
    const next = form.body.slice(0, start) + token + form.body.slice(end);
    setForm((f: any) => ({ ...f, body: next }));
    setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = start + token.length; }, 0);
  };

  const save = async () => {
    const payload = { name: form.name, channel: form.channel, category: form.category, visibility: form.visibility, subject: form.subject || null, body: form.body };
    if (form.id) await updateTemplate({ id: form.id, body: payload }).unwrap();
    else await createTemplate(payload).unwrap();
    reset();
  };

  const runPreview = async () => {
    const res: any = await preview({ subject: form.subject, body: form.body }).unwrap();
    setRendered(res.data);
    setTab(1);
  };

  const runAi = async () => {
    const prompt = window.prompt("Describe the message you want (AI is a placeholder for now):");
    if (!prompt) return;
    const res: any = await aiGenerate({ prompt, channel: form.channel }).unwrap();
    setForm((f: any) => ({ ...f, subject: res.data.subject ?? f.subject, body: res.data.body }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={2}>Communication Templates</Typography>
      <Grid container spacing={2}>
        {/* List */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 1 }}>
            <Button fullWidth startIcon={<AddIcon />} onClick={reset} sx={{ mb: 1 }}>New Template</Button>
            <Stack spacing={0.5} sx={{ maxHeight: "70vh", overflowY: "auto" }}>
              {templates.map((t: any) => (
                <Paper key={t.id} variant="outlined" sx={{ p: 1, cursor: "pointer", bgcolor: form.id === t.id ? "action.selected" : undefined }} onClick={() => edit(t)}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box minWidth={0}>
                      <Typography variant="body2" fontWeight={600} noWrap>{t.name}</Typography>
                      <Stack direction="row" spacing={0.5} mt={0.5}>
                        <Chip size="small" label={t.channel} />
                        {!t.company_id && <Chip size="small" color="primary" label="system" />}
                      </Stack>
                    </Box>
                    <Box>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); edit(t); }}><EditIcon fontSize="small" /></IconButton>
                      {t.company_id && <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); deleteTemplate(t.id); }}><DeleteIcon fontSize="small" /></IconButton>}
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Paper>
        </Grid>

        {/* Builder */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
                <TextField select label="Channel" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} sx={{ width: 140 }}>
                  {CHANNELS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField select label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} fullWidth>
                  {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
                <TextField select label="Visibility" value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })} sx={{ width: 160 }}>
                  <MenuItem value="customer">Customer</MenuItem>
                  <MenuItem value="internal">Internal</MenuItem>
                </TextField>
              </Stack>

              <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                <Tab label="Editor" /><Tab label="Preview" />
              </Tabs>

              {tab === 0 ? (
                <>
                  {form.channel === "email" && (
                    <TextField label="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} fullWidth />
                  )}
                  <TextField label="Body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
                    fullWidth multiline minRows={10} inputRef={bodyRef} />
                  <Stack direction="row" spacing={1}>
                    <Button variant="contained" onClick={save} disabled={!form.name || !form.body}>{form.id ? "Update" : "Create"}</Button>
                    <Button onClick={runPreview} disabled={!form.body}>Preview</Button>
                    <Button startIcon={<AutoAwesomeIcon />} onClick={runAi} disabled={aiLoading}>Generate with AI</Button>
                  </Stack>
                </>
              ) : (
                <Box sx={{ minHeight: 240, p: 2, bgcolor: "#f5f6f8", borderRadius: 1 }}>
                  {rendered ? (
                    <>
                      {rendered.subject ? <Typography fontWeight={700} mb={1}>{rendered.subject}</Typography> : null}
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{rendered.body}</Typography>
                    </>
                  ) : <Typography color="text.secondary">Click Preview to render with sample values.</Typography>}
                </Box>
              )}
            </Stack>
          </Paper>
        </Grid>

        {/* Variable picker */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, maxHeight: "80vh", overflowY: "auto" }}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>Variables</Typography>
            <Typography variant="caption" color="text.secondary">Click to insert at cursor.</Typography>
            {Object.keys(grouped).map((cat) => (
              <Box key={cat} mt={1.5}>
                <Typography variant="subtitle2" textTransform="capitalize">{cat}</Typography>
                <Divider sx={{ mb: 0.5 }} />
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {grouped[cat].map((v: any) => (
                    <Chip key={v.key} size="small" variant="outlined" label={v.key} onClick={() => insertVariable(v.key)} sx={{ cursor: "pointer" }} />
                  ))}
                </Stack>
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Templates;
